import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from "react-query";
import TableOrganism from '../../organisms/Table/TableOrganism';
import { createHeaderRow } from '../../utilities/CreateHeaderRow';
import Loader from '../../atoms/Loader/LoaderAtom';
import { useLoaderDispatch, useLoaderState } from '../../context/LoaderContext';
import { startLoading, stopLoading } from "../../context/actions/loaderActions";
import { useHistory, useParams } from 'react-router-dom';
import { TableRowProps } from '../../molecules/TableRow/TableRowAtom';
import { createLabelsRows } from './CreateLabelsTable'
import { CallToActionButton } from '../../atoms/callToActionButton/CallToActionButton';
import { largeButton } from '../../utilities/style';
import useApiService from '../../utilities/useApiService';
import PrintHeaderInfo from '../../organisms/PrintHeaderInfo/PrintHeaderInfo';
import { useAuthState } from '../../context/AuthContext';
import EditShipmentDialog, { EditableShipment } from './EditShipmentDialog';
import AreYouSure from '../../molecules/AreYouSure/AreYouSure';
import { isAdmin } from '../../utilities/checkUserRole';
import { toastFail } from '../../utilities/toast';
import { getApiErrorMessageOrFallback } from '../../utilities/apiErrors';


export interface LabelsPageProps { }

const LabelsPage: React.FC<LabelsPageProps> = (
    props: LabelsPageProps
) => {
    const { getMethod, putMethod, deleteMethod } = useApiService();
    const history = useHistory();
    const params: any = useParams();
    const { pharmacyId, requestId } = params;
    const loaderDispatch = useLoaderDispatch();
    const { loading } = useLoaderState();
    const { user } = useAuthState();
    const userRole = user!.role;
    const queryClient = useQueryClient();
    const [shipmentToEdit, setShipmentToEdit] = useState<EditableShipment | null>(null);
    const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
    const [toBeDeletedObject, setToBeDeletedObject] = useState<{ id: number } | null>(null);

    const headerTitles = isAdmin(userRole)
      ? ['Pickup Date', 'Tracking Number', 'Shipping Label', 'Status', 'Edit', 'Delete']
      : ['Pickup Date', 'Tracking Number', 'Shipping Label', 'Status'];

    const fetchShipments = async () => {
        let response;
        startLoading(loaderDispatch);
        response = await getMethod(`/pharmacies/${pharmacyId}/returnrequests/${requestId}/shipments`);
        stopLoading(loaderDispatch);
        return response.data;
    }
    const { data: shipments, refetch } = useQuery('shipments', fetchShipments);

    const handleCancellation = async (entry: any) => {
      startLoading(loaderDispatch);
      const response = await putMethod(
        `/pharmacies/${pharmacyId}/returnrequests/${requestId}/shipments/${entry.id}`
      );
      if (response.status >= 200 && response.status < 300) {
        await refetch();
        stopLoading(loaderDispatch);
      } else {
        stopLoading(loaderDispatch);
        const errorMessage =
          response.data ||
          "Cancellation Failed: The void window has expired or the package is already in transit.";
        alert(errorMessage);
      }
    };

    const deleteOfflineShipmentApi = async ({ id }: { id: number }) => {
      startLoading(loaderDispatch);
      await deleteMethod(
        `/pharmacies/${pharmacyId}/returnrequests/${requestId}/shipments/${id}/offline`
      );
      stopLoading(loaderDispatch);
      return id;
    };

    const { mutate: deleteOfflineShipment } = useMutation(deleteOfflineShipmentApi, {
      onSuccess: () => {
        queryClient.invalidateQueries('shipments');
        setOpenConfirmDelete(false);
        setToBeDeletedObject(null);
      },
      onError: (error) => {
        toastFail(
          getApiErrorMessageOrFallback(error, 'Failed to delete offline shipment')
        );
      },
      onSettled: () => stopLoading(loaderDispatch),
    });

    const renderPharmacyInfo = () => {
      if (shipments && shipments.length > 0) {
        return (
          <PrintHeaderInfo
            pharmacyName={shipments[0].returnRequest.pharmacy.doingBusinessAs}
          />
        );
      }
      return null;
    };

    const rows: Array<TableRowProps> = shipments
        ? createLabelsRows(
            shipments,
            history,
            handleCancellation,
            userRole,
            (entry) => setShipmentToEdit({
              id: entry.id,
              trackingNumber: entry.trackingNumber,
              offlineUploaded: entry.offlineUploaded,
            }),
            (entry) => {
              setToBeDeletedObject({ id: entry.id });
              setOpenConfirmDelete(true);
            }
        )
        : [];

    return loading ? (
      <Loader />
    ) : (
      <div>
        {renderPharmacyInfo()}
        <TableOrganism
          header={createHeaderRow([...headerTitles])}
          rows={rows}
        />
        <div style={{ marginTop: 20, marginBottom: 20 }}>
          <CallToActionButton
            height={"80px"}
            text="Create New Shipment"
            width={largeButton.width}
            onClick={() => {
              history.location.pathname.indexOf("admin") !== -1
                ? history.push(
                    `/admin/pharmacies/${pharmacyId}/returnrequests/${requestId}/shipments/create`
                  )
                : history.push(
                    `/pharmacies/${pharmacyId}/returnrequests/${requestId}/shipments/create`
                  );
            }}
          />
        </div>
        {shipmentToEdit && (
          <EditShipmentDialog
            open={Boolean(shipmentToEdit)}
            shipment={shipmentToEdit}
            onClose={() => setShipmentToEdit(null)}
          />
        )}
        {openConfirmDelete && toBeDeletedObject && (
          <AreYouSure
            open={openConfirmDelete}
            onClose={() => {
              setOpenConfirmDelete(false);
              setToBeDeletedObject(null);
            }}
            toBeDeletedObject={toBeDeletedObject}
            deleteObject={deleteOfflineShipment}
          />
        )}
      </div>
    );
};

export default LabelsPage;


// const labels = [
//     {
//         pickupDay: '7/2/2022',
//         trackingNumber: '#12313123',
//         label: 'View Shipping Label',
//         status: 'Delivered'
//     },
//     {
//         pickupDay: '7/2/2022',
//         trackingNumber: '#12313123',
//         label: 'View Shipping Label',
//         status: 'Delivered'
//     },
//     {
//         pickupDay: '7/2/2022',
//         trackingNumber: '#12313123',
//         label: 'View Shipping Label',
//         status: 'Delivered'
//     }
// ]